import React, { JSX, useEffect, useMemo, useState } from 'react'
import { AnalyticsMetafield, CountryTotals } from 'types/api'
import { cleanVariables, formatPrice, getCountryNameFromCode, graphqlRequest, numberFormatter } from 'lib/helpers'
import { useQuery } from '@tanstack/react-query'
import { ExportModal, SortableHeader, sortData } from './SalesTable'
import { EmptyDataCard } from './EmptyDataCard'
import { RefreshingBlock } from './Loading'
import getCountryTotals from '../graphql/getCountryTotals.graphql'

export interface CountrySalesTableProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  token?: string
  client: AnalyticsMetafield | null
}

export const CountrySalesTable = ({ open, setOpen, token, client, filters }: CountrySalesTableProps): JSX.Element => {
  const PAGE_SIZE = 10
  const [page, setPage] = useState(1)
  const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' }>({ key: 'sales', direction: 'desc' })
  const idField = 'country'
  const nameField = 'countryName'
  const {
    data,
    isFetching
  } = useQuery({
    queryKey: ['dashboard', 'countryTotals', filters, client?.id],

    queryFn: async () => {
      if (token == null) return

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: filters?.productOption?.map(p => p.id),
        startDate: filters.range.startDate,
        endDate: filters.range.endDate
      })
      const { countryTotals } = await graphqlRequest<{ countryTotals: CountryTotals[] }>(
        getCountryTotals, cleanVar, token
      )

      return countryTotals
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const countryNames = data?.map(r => ({
    ...r,
    countryName: getCountryNameFromCode(r.country)
  }))

  const sortedData = useMemo(() => sortData(sortConfig, countryNames), [countryNames, sortConfig])
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  useEffect(() => setPage(1), [sortConfig, sortedData.length])

  const total = sortedData.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const startIdx = (page - 1) * PAGE_SIZE
  const endIdx = startIdx + PAGE_SIZE
  const pagedData = sortedData.slice(startIdx, endIdx)

  const canPrev = page > 1
  const canNext = page < totalPages

  const handleSort = (key: string): void => {
    setSortConfig(prev =>
      prev?.key === key && prev.direction === 'asc'
        ? { key, direction: 'desc' }
        : { key, direction: 'asc' }
    )
  }

  if (!isFetching && (countryNames?.length ?? 0) === 0) {
    return <EmptyDataCard message='No country data available' />
  }

  if (isFetching) {
    return (
      <div className='relative'>
        {RefreshingBlock}
        <EmptyDataCard message='No country data available' />
      </div>
    )
  }

  const fillerCount = PAGE_SIZE - pagedData.length

  return (
    <div data-testid='country-sales-table' className='mt-4 md:mx-0 shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden'>
      <ExportModal tableType='country' open={open} setOpen={setOpen} data={countryNames ?? []} />
      <table data-testid='sales-table' className='w-full text-left whitespace-nowrap'>
        <colgroup>
          <col className='w-full w-6/12' />
          <col className='lg:w-3/12' />
        </colgroup>
        <thead className='border-b border-lm-bg-accent dark:border-dm-bg-accent text-sm/6 '>
          <tr>
            <SortableHeader
              first
              label='Country'
              sortKey='country'
              sortConfig={sortConfig}
              onSort={handleSort}
            />
            <SortableHeader
              label='Quantity'
              sortKey='quantity'
              sortConfig={sortConfig}
              onSort={handleSort}
            />

            <SortableHeader
              label='Sales'
              sortKey='sales'
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </tr>
        </thead>
        <tbody className='divide-y divide-lm-bg-secondary dark:divide-dm-bg-secondary'>
          {pagedData.map((item) => (
            <tr
              data-testid={`row-${String(item[idField])}`}
              key={item[idField]}
              onClick={() =>
                setExpandedRow(expandedRow === item[idField] ? null : item[idField])}
              className='cursor-pointer md:cursor-default transition-none duration-200 hover:bg-lm-bg-secondary dark:hover:bg-dm-bg-secondary'
            >
              <td className='py-4 pr-4 pl-0 sm:pr-8'>
                <div className={`text-sm/6 font-medium pl-4 sm:pl-6 transition-none duration-200 ${
    expandedRow === item[idField] ? 'whitespace-normal' : 'overflow-hidden text-ellipsis'}  w-[33vw] max-w-[33vw] sm:w-auto sm:max-w-none`}
                >
                  {item[nameField]}
                </div>
              </td>
              <td className='py-4 pr-6 pl-4 sm:pl-6 sm:pr-8 text-sm/6 font-medium'>
                {numberFormatter(item.quantity)}
              </td>

              <td className='py-4 pr-6 sm:pl-6 sm:pr-8 text-sm/6 font-medium'>
                {formatPrice(item.sales)}
              </td>
            </tr>
          ))}
          {Array.from({ length: fillerCount ?? 0 }).map((_, i) => (
            <tr key={`filler-${i}`}>
              <td className='py-4 px-4'>&nbsp;</td>
              <td className='py-4 px-4' />
              <td className='py-4 px-4' />
            </tr>
          ))}
        </tbody>
      </table>
      <div className='flex items-center justify-between border-t border-lm-bg-accent dark:border-dm-bg-accent px-4 sm:px-6 py-3 text-sm'>
        <div className='text-lm-text-secondary dark:text-dm-text-secondary'>
          Showing <span className='font-medium'>{startIdx + 1}</span>–
          <span className='font-medium'>{Math.min(endIdx, total)}</span> of{' '}
          <span className='font-medium'>{total}</span>
        </div>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={!canPrev}
            className='px-3 py-1.5 rounded-lg border border-lm-text-secondary dark:border-dm-text-secondary disabled:opacity-40'
          >
            Prev
          </button>

          <button
            type='button'
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={!canNext}
            className='px-3 py-1.5 rounded-lg border border-lm-text-secondary dark:border-dm-text-secondary disabled:opacity-40'
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
